<script setup lang="ts">
import nacl from 'tweetnacl';
import { COOKIEDOMAIN, OAUTH_TOKEN } from '@/utils/constants';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiSocialLoginProps {
  captureClick?: boolean;
  redirectTo?: object | null;
}
const props = withDefaults(defineProps<CiSocialLoginProps>(), {
  captureClick: false,
  redirectTo: null,
});

const emit = defineEmits<{
  callback: [value: string];
}>();

/********************
 * COMPOSITIONS     *
 ********************/
const appStore = useAppStore();
const userStore = useUserStore();
const loadingStore = useLoadingStore();
const localePath = useLocalePath();
const runtimeConfig = useRuntimeConfig();
const route = useRoute();
const oauthTokenCookie = useCookie<string | null>(OAUTH_TOKEN, { sameSite: 'strict', secure: true, ...(COOKIEDOMAIN && { domain: COOKIEDOMAIN }) });

/********************
 * FUNCTIONS        *
 ********************/
function performOAuthLogin(urlTemplate: string, clienId: string, provider: string) {
  oauthTokenCookie.value = nacl.randomBytes(64).join('');
  loadingStore.setStatusLoading('login');
  let redirectTo = '';
  if (props.redirectTo) {
    redirectTo = encodeURIComponent('json:' + btoa(JSON.stringify(props.redirectTo)));
  }
  const redirectUri = `${runtimeConfig.public.oAuthBaseUrl}${localePath({ name: 'oauth' })}`;
  let redirectParam = redirectTo || localePath(route.query.redirect as string || '/');
  if (redirectParam.endsWith('/oauth')) {
    redirectParam = localePath('/');
  }
  const state = `${redirectParam};${oauthTokenCookie.value};${provider};${userStore.insiderUid || ''}`;
  const oAuthUrl = urlTemplate.replace('{clientId}', clienId).replace('{redirectUri}', redirectUri).replace('{state}', state).replace('{nonce}', oauthTokenCookie.value);
  if (props.captureClick) {
    emit('callback', oAuthUrl);
    return;
  }
  navigateTo(oAuthUrl, { external: true });
}

function googleLogin() {
  performOAuthLogin('https://accounts.google.com/o/oauth2/auth?client_id={clientId}&redirect_uri={redirectUri}&state={state}&scope=profile%20email&response_type=code&include_granted_scopes=true&access_type=offline', runtimeConfig.public.googleOauthClientId, 'google');
}
function appleLogin() {
  performOAuthLogin('https://appleid.apple.com/auth/authorize?client_id={clientId}&redirect_uri={redirectUri}&state={state}&response_type=code&scope=name%20email&response_mode=form_post&nonce={nonce}', runtimeConfig.public.appleOauthClientId, 'apple');
}
function facebookLogin() {
  performOAuthLogin('https://www.facebook.com/v22.0/dialog/oauth?client_id={clientId}&redirect_uri={redirectUri}&state={state}&response_type=code&scope=email,public_profile', runtimeConfig.public.facebookOauthClientId, 'facebook');
}
</script>

<template>
  <div v-if="appStore.showSocialLogin">
    <div class="horizontal-buttons mt-6 flex justify-around">
      <CiButton
        tag="button"
        variant="text"
        :disabled="loadingStore.isLoading('login')"
        class="rounded border border-gray px-6 py-4 hover:border-dark-green"
        @click="googleLogin()"
      >
        <img
          src="@/assets/svg/google-logo.svg"
          alt="Google"
          title="Continue with Google"
          width="24"
          height="24"
        />
      </CiButton>
      <CiButton
        tag="button"
        variant="text"
        :disabled="loadingStore.isLoading('login')"
        class="rounded border border-gray px-6 py-4 hover:border-dark-green"
        @click="facebookLogin()"
      >
        <img
          src="@/assets/svg/facebook-logo.svg"
          alt="Facebook"
          title="Continue with Facebook"
          width="24"
          height="24"
        />
      </CiButton>
      <CiButton
        tag="button"
        variant="text"
        :disabled="loadingStore.isLoading('login')"
        class="rounded border border-gray px-6 py-4 hover:border-dark-green"
        @click="appleLogin()"
      >
        <img
          src="@/assets/svg/apple-logo.svg"
          alt="Apple"
          title="Continue with Apple"
          width="20"
          height="24"
        />
      </CiButton>
    </div>
    <div class="inline-flex w-full items-center justify-center">
      <hr class="border-1 my-8 w-64 opacity-40" />
      <span class="absolute left-1/2 -translate-x-1/2 bg-white px-3 text-sm">{{ $gettext('or') }}</span>
    </div>
  </div>
  <div v-else class="pointer-events-none mb-6" />
</template>

<style lang="postcss" scoped>
.horizontal-buttons button:disabled,
.horizontal-buttons button:disabled:hover {
  @apply border-gray-light grayscale bg-transparent;
}
</style>
